import { z } from "zod";

/** The PWA's locales; the push text is localized per stored language. */
export const PUSH_LANGS = ["nl", "en", "de", "fr", "it", "es", "pt"] as const;
export const PushLang = z.enum(PUSH_LANGS);
export type PushLang = z.infer<typeof PushLang>;

/**
 * `POST /v1/push/subscribe`: a browser's `PushSubscription.toJSON()` plus the
 * UI language. The endpoint must be https (push services are, and the Worker
 * posts to whatever is stored here); the keys are base64url as the browser
 * hands them out.
 */
export const PushSubscribeRequest = z.object({
  endpoint: z.url({ protocol: /^https$/ }).max(2048),
  keys: z.object({
    p256dh: z.string().regex(/^[A-Za-z0-9_-]{16,256}$/),
    auth: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/),
  }),
  lang: PushLang,
});
export type PushSubscribeRequest = z.infer<typeof PushSubscribeRequest>;

/** `DELETE /v1/push/subscribe`: the subscription is identified by its endpoint alone. */
export const PushUnsubscribeRequest = z.object({
  endpoint: z.url({ protocol: /^https$/ }).max(2048),
});
export type PushUnsubscribeRequest = z.infer<typeof PushUnsubscribeRequest>;

/**
 * What a push carries: one generic, localized sentence and nothing about the
 * person or their tastings. The service worker shows it as is.
 */
export const PushPayload = z.object({ title: z.string().min(1), body: z.string().min(1) });
export type PushPayload = z.infer<typeof PushPayload>;

/** The sentence per language, our own words; the producer's name is the only thing a push can say about a person's request. */
const PRODUCER_ADDED: Readonly<Record<PushLang, (producer: string) => string>> = {
  nl: (p) => `${p} is aan de catalogus toegevoegd.`,
  en: (p) => `${p} has been added to the catalogue.`,
  de: (p) => `${p} wurde zum Katalog hinzugefügt.`,
  fr: (p) => `${p} a été ajouté au catalogue.`,
  it: (p) => `${p} è stato aggiunto al catalogo.`,
  es: (p) => `${p} se ha añadido al catálogo.`,
  pt: (p) => `${p} foi adicionado ao catálogo.`,
};

/** The push for a winery druif added after a device asked for it: the producer's name in the device's language. */
export function pushProducerAddedText(lang: PushLang, producer: string): PushPayload {
  // nosemgrep: rules.javascript.lang.security.audit.unsafe-dynamic-method -- lang is the closed PushLang union, so the lookup can only reach one of the seven sentence builders above.
  return { title: "proef", body: PRODUCER_ADDED[lang](producer) };
}
